import React, { useMemo, useState, useEffect, useCallback } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    FiUsers, FiSearch, FiEdit2, FiLock, FiBan, FiCheckCircle,
    FiPhone, FiMail, FiCalendar, FiTag, FiGift, FiRefreshCw,
    FiChevronLeft, FiChevronRight, FiAlertTriangle, FiUser,
    FiX, FiFilter
} from "react-icons/fi";
import { usersAPI } from "services/api";
import EditCustomerModal from "./components/EditCustomerModal";
import {BsBan} from "react-icons/bs";

// ============================================================
// SABITLER
// ============================================================
const PAGE_SIZE = 10;

// ============================================================
// YARDIMCI FONKSİYONLAR
// ============================================================
const formatDate = (dateString) => {
    if (!dateString) return "—";
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "—";
        return date.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return "—"; }
};

const getInitials = (name = "", surname = "") =>
    ((name[0] || "") + (surname[0] || "")).toUpperCase() || "?";

const AVATAR_COLORS = [
    "from-brand-400 to-purple-500",
    "from-blue-400 to-cyan-500",
    "from-emerald-400 to-teal-500",
    "from-amber-400 to-orange-500",
    "from-pink-400 to-rose-500",
];
const getAvatarColor = (id) => AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length];

// ============================================================
// SKELETON LOADER
// ============================================================
const SkeletonRow = () => (
    <div className="animate-pulse flex items-center gap-4 p-4 border-b border-gray-100 dark:border-navy-700">
        <div className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-navy-700 shrink-0"></div>
        <div className="flex-1 space-y-2">
            <div className="h-3 bg-gray-200 dark:bg-navy-700 rounded-full w-1/3"></div>
            <div className="h-2 bg-gray-200 dark:bg-navy-700 rounded-full w-1/2"></div>
        </div>
        <div className="hidden sm:flex gap-2">
            <div className="h-3 bg-gray-200 dark:bg-navy-700 rounded-full w-24"></div>
            <div className="h-3 bg-gray-200 dark:bg-navy-700 rounded-full w-20"></div>
        </div>
        <div className="flex gap-2">
            <div className="w-8 h-8 rounded-xl bg-gray-200 dark:bg-navy-700"></div>
            <div className="w-8 h-8 rounded-xl bg-gray-200 dark:bg-navy-700"></div>
            <div className="w-8 h-8 rounded-xl bg-gray-200 dark:bg-navy-700"></div>
        </div>
    </div>
);

const SkeletonCard = () => (
    <div className="animate-pulse p-4 rounded-2xl border border-gray-100 dark:border-navy-700 bg-white dark:bg-navy-800 space-y-3">
        <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gray-200 dark:bg-navy-700 shrink-0"></div>
            <div className="flex-1 space-y-2">
                <div className="h-3 bg-gray-200 dark:bg-navy-700 rounded-full w-3/4"></div>
                <div className="h-2 bg-gray-200 dark:bg-navy-700 rounded-full w-1/2"></div>
            </div>
        </div>
        <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-navy-700">
            <div className="flex justify-between">
                <div className="h-2 bg-gray-200 dark:bg-navy-700 rounded-full w-16"></div>
                <div className="h-2 bg-gray-200 dark:bg-navy-700 rounded-full w-24"></div>
            </div>
            <div className="flex justify-between">
                <div className="h-2 bg-gray-200 dark:bg-navy-700 rounded-full w-20"></div>
                <div className="h-2 bg-gray-200 dark:bg-navy-700 rounded-full w-16"></div>
            </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
            {[1,2,3].map(i => <div key={i} className="w-8 h-8 rounded-xl bg-gray-200 dark:bg-navy-700"></div>)}
        </div>
    </div>
);

// ============================================================
// PAGINATION BILEŞENI
// ============================================================
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, pageStart, pageEnd }) => {
    if (totalPages <= 1) return null;

    const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
        .reduce((acc, p, idx, arr) => {
            if (idx === 0) return [p];
            if (p - arr[idx - 1] > 1) acc.push(-1);
            acc.push(p);
            return acc;
        }, []);

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 dark:border-navy-700">
      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
        <span className="font-bold text-navy-700 dark:text-white">{pageStart}–{pageEnd}</span> / {totalItems} kayıt
      </span>
            <div className="flex items-center gap-1">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <FiChevronLeft size={15} />
                </button>

                {pages.map((p, idx) =>
                    p === -1 ? (
                        <span key={`sep-${idx}`} className="w-8 h-8 flex items-center justify-center text-gray-400 text-sm">…</span>
                    ) : (
                        <button
                            key={p}
                            onClick={() => onPageChange(p)}
                            className={`w-8 h-8 rounded-xl text-sm font-bold transition-all
                ${p === currentPage
                                ? "bg-brand-500 text-white shadow-md shadow-brand-500/30"
                                : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700"}`}
                        >
                            {p}
                        </button>
                    )
                )}

                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-8 h-8 rounded-xl flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <FiChevronRight size={15} />
                </button>
            </div>
        </div>
    );
};

// ============================================================
// MASAÜSTÜ TABLO SATIRI
// ============================================================
const CustomerTableRow = ({ c, onEdit, onResetPassword, onToggleBlacklist, resettingPasswordId }) => {
    const fullName = `${c.name || ""} ${c.surname || ""}`.trim() || "—";
    const isResetting = resettingPasswordId === c.id;

    return (
        <tr className="border-b border-gray-100 dark:border-navy-700 hover:bg-gray-50/50 dark:hover:bg-navy-700/30 transition-colors group">
            {/* Müşteri Bilgisi */}
            <td className="px-5 py-3.5">
                <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${getAvatarColor(c.id)} flex items-center justify-center text-white font-bold text-xs shrink-0 shadow-sm`}>
                        {getInitials(c.name, c.surname)}
                    </div>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-sm text-navy-700 dark:text-white">{fullName}</span>
                            {c.tag && (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 flex items-center gap-1">
                  <FiTag size={9} /> {c.tag}
                </span>
                            )}
                            {c.is_blacklisted && (
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                  Kara Liste
                </span>
                            )}
                        </div>
                        {c.email && c.email !== "mail@giriniz.com" && c.email !== "hizlikayit@randevupp.com" && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-0.5">
                                <FiMail size={10} /> {c.email}
                            </div>
                        )}
                    </div>
                </div>
            </td>

            {/* Telefon */}
            <td className="px-5 py-3.5">
                {c.phone ? (
                    <a
                        href={`tel:${c.phone}`}
                        className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-brand-500 dark:hover:text-brand-400 transition-colors font-medium"
                    >
                        <FiPhone size={13} className="text-gray-400" /> {c.phone}
                    </a>
                ) : (
                    <span className="text-gray-300 dark:text-gray-600 text-sm">—</span>
                )}
            </td>

            {/* Doğum Tarihi */}
            <td className="px-5 py-3.5">
                <span className="text-sm text-gray-600 dark:text-gray-300">{formatDate(c.birth_date)}</span>
            </td>

            {/* Kayıt Tarihi */}
            <td className="px-5 py-3.5">
                <span className="text-sm text-gray-600 dark:text-gray-300">{formatDate(c.joinDate)}</span>
            </td>

            {/* Durum */}
            <td className="px-5 py-3.5">
                {c.is_blacklisted ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400">
            <BsBan size={11} /> Kara Liste
          </span>
                ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400">
            <FiCheckCircle size={11} /> Aktif
          </span>
                )}
            </td>

            {/* İşlemler */}
            <td className="px-5 py-3.5">
                <div className="flex items-center justify-center gap-1.5">
                    {/* Şifre Sıfırla */}
                    <button
                        title="Şifreyi Sıfırla (123456!)"
                        onClick={() => onResetPassword(c)}
                        disabled={isResetting}
                        className="w-8 h-8 rounded-xl flex items-center justify-center text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 border border-transparent hover:border-amber-200 dark:hover:border-amber-800/40 transition-all disabled:opacity-50"
                    >
                        {isResetting
                            ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-amber-500"></div>
                            : <FiLock size={14} />}
                    </button>

                    {/* Kara Liste Toggle */}
                    <button
                        title={c.is_blacklisted ? "Yasağı Kaldır" : "Kara Listeye Ekle"}
                        onClick={() => onToggleBlacklist(c)}
                        className={`w-8 h-8 rounded-xl flex items-center justify-center border border-transparent transition-all
              ${c.is_blacklisted
                            ? "text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-200 dark:hover:border-green-800/40"
                            : "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-200 dark:hover:border-red-800/40"}`}
                    >
                        {c.is_blacklisted ? <FiCheckCircle size={14} /> : <BsBan size={14} />}
                    </button>

                    {/* Düzenle */}
                    <button
                        title="Düzenle"
                        onClick={() => onEdit(c)}
                        className="w-8 h-8 rounded-xl flex items-center justify-center text-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/20 border border-transparent hover:border-brand-200 dark:hover:border-brand-800/40 transition-all"
                    >
                        <FiEdit2 size={14} />
                    </button>
                </div>
            </td>
        </tr>
    );
};

// ============================================================
// MOBİL MÜŞTERİ KARTI
// ============================================================
const CustomerMobileCard = ({ c, onEdit, onResetPassword, onToggleBlacklist, resettingPasswordId }) => {
    const fullName = `${c.name || ""} ${c.surname || ""}`.trim() || "—";
    const isResetting = resettingPasswordId === c.id;

    return (
        <div className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-100 dark:border-navy-700 overflow-hidden hover:shadow-md transition-all">
            {/* Üst: Avatar + İsim + Durum */}
            <div className="flex items-center gap-3 p-4 border-b border-gray-100 dark:border-navy-700">
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${getAvatarColor(c.id)} flex items-center justify-center text-white font-bold shrink-0 shadow-sm`}>
                    {getInitials(c.name, c.surname)}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-navy-700 dark:text-white text-sm truncate">{fullName}</div>
                    {c.tag && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 mt-0.5">
              <FiTag size={9} /> {c.tag}
            </span>
                    )}
                    {c.email && c.email !== "mail@giriniz.com" && c.email !== "hizlikayit@randevupp.com" && (
                        <div className="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5">{c.email}</div>
                    )}
                </div>
                {c.is_blacklisted ? (
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 shrink-0">Kara Liste</span>
                ) : (
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400 shrink-0">Aktif</span>
                )}
            </div>

            {/* Detaylar */}
            <div className="px-4 py-3 space-y-2">
                {c.phone && (
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400 dark:text-gray-500 font-medium">Telefon</span>
                        <a href={`tel:${c.phone}`} className="font-bold text-navy-700 dark:text-white flex items-center gap-1 hover:text-brand-500 transition-colors">
                            <FiPhone size={11} className="text-gray-400" /> {c.phone}
                        </a>
                    </div>
                )}
                {c.birth_date && (
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400 dark:text-gray-500 font-medium">Doğum Tarihi</span>
                        <span className="font-bold text-navy-700 dark:text-white">{formatDate(c.birth_date)}</span>
                    </div>
                )}
                {c.joinDate && (
                    <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-400 dark:text-gray-500 font-medium">Kayıt Tarihi</span>
                        <span className="font-bold text-navy-700 dark:text-white">{formatDate(c.joinDate)}</span>
                    </div>
                )}
            </div>

            {/* Aksiyon Butonları */}
            <div className="px-4 py-3 border-t border-gray-100 dark:border-navy-700 flex justify-end gap-2">
                <button
                    onClick={() => onResetPassword(c)}
                    disabled={isResetting}
                    title="Şifreyi Sıfırla"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40 hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-all disabled:opacity-50"
                >
                    {isResetting
                        ? <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-amber-500"></div>
                        : <FiLock size={12} />}
                    Şifre
                </button>
                <button
                    onClick={() => onToggleBlacklist(c)}
                    title={c.is_blacklisted ? "Yasağı Kaldır" : "Kara Listeye Ekle"}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all
            ${c.is_blacklisted
                        ? "text-green-600 dark:text-green-400 border-green-200 dark:border-green-800/40 hover:bg-green-50 dark:hover:bg-green-900/10"
                        : "text-red-600 dark:text-red-400 border-red-200 dark:border-red-800/40 hover:bg-red-50 dark:hover:bg-red-900/10"}`}
                >
                    {c.is_blacklisted ? <><FiCheckCircle size={12} /> Aktifleştir</> : <><BsBan size={12} /> Yasakla</>}
                </button>
                <button
                    onClick={() => onEdit(c)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-brand-600 dark:text-brand-400 border border-brand-200 dark:border-brand-800/40 hover:bg-brand-50 dark:hover:bg-brand-900/10 transition-all"
                >
                    <FiEdit2 size={12} /> Düzenle
                </button>
            </div>
        </div>
    );
};

// ============================================================
// DOĞUM GÜNÜ KARTI (Küçük)
// ============================================================
const BirthdayCard = ({ c }) => (
    <div className="flex items-center gap-3 p-3 rounded-2xl bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-800/30 hover:shadow-sm transition-all">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold text-xs shrink-0 shadow-sm">
            {getInitials(c.name, c.surname)}
        </div>
        <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-navy-700 dark:text-white truncate">
                {c.name} {c.surname}
                <span className="ml-1.5 text-base">🎉</span>
            </div>
            {c.phone && (
                <a href={`tel:${c.phone}`} className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 mt-0.5 hover:text-amber-700 transition-colors">
                    <FiPhone size={10} /> {c.phone}
                </a>
            )}
        </div>
    </div>
);

// ============================================================
// ANA BILEŞEN
// ============================================================
const AdminCustomerList = () => {
    // --- State ---
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [q, setQ] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [activeTab, setActiveTab] = useState("active");

    // Modal
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [formData, setFormData] = useState({
        id: null, name: "", surname: "", phone: "", email: "", birth_date: "", tag: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [modalError, setModalError] = useState(null);

    // Şifre sıfırlama
    const [resettingPasswordId, setResettingPasswordId] = useState(null);

    // ============================================================
    // VERİ ÇEKME
    // ============================================================
    const fetchCustomers = useCallback(async () => {
        try {
            setLoading(true); setError(null);
            const response = await usersAPI.getCustomers();
            if (!response || !response.customers) throw new Error("Geçersiz API yanıtı");

            const formatted = response.customers.map(person => ({
                id: person?.id || Date.now() + Math.random(),
                name: person?.name || "",
                surname: person?.surname || "",
                phone: person?.phone || null,
                email: person?.email || null,
                birth_date: person?.birth_date ? person.birth_date.split("T")[0] : null,
                joinDate: person?.created_at ? new Date(person.created_at).toISOString().split("T")[0] : null,
                status: person?.status || "active",
                tag: person?.tag || null,
                is_blacklisted: person?.is_blacklisted == 1 || person?.is_blacklisted === true,
            }));

            setCustomers(formatted);
        } catch (err) {
            console.error("Müşteri verileri yüklenirken hata:", err);
            setError(err?.message || "Müşteri verileri yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

    // Tab değişince page reset
    useEffect(() => { setCurrentPage(1); }, [activeTab, q]);

    // ============================================================
    // FİLTRELEME & SAYFALAMA
    // ============================================================
    const filteredCustomers = useMemo(() => {
        if (!customers?.length) return [];
        const query = q.toLowerCase();
        return customers.filter(c => {
            if (activeTab === "active" && c.is_blacklisted) return false;
            if (activeTab === "blacklisted" && !c.is_blacklisted) return false;
            const fullName = `${c.name || ""} ${c.surname || ""}`.toLowerCase();
            return (
                fullName.includes(query) ||
                (c.phone && String(c.phone).includes(query)) ||
                (c.email && c.email.toLowerCase().includes(query))
            );
        });
    }, [customers, q, activeTab]);

    const totalItems = filteredCustomers.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
    const pageStart = totalItems ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
    const pageEnd = Math.min(currentPage * PAGE_SIZE, totalItems);

    const pagedCustomers = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredCustomers.slice(start, start + PAGE_SIZE);
    }, [filteredCustomers, currentPage]);

    // ============================================================
    // BUGÜN DOĞUM GÜNÜ
    // ============================================================
    const birthdayCustomers = useMemo(() => {
        if (!customers?.length) return [];
        const today = new Date();
        const todayMonthDay = `${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
        return customers.filter(c => {
            if (!c.birth_date || typeof c.birth_date !== "string") return false;
            try { return c.birth_date.substring(5) === todayMonthDay; }
            catch { return false; }
        });
    }, [customers]);

    // ============================================================
    // HANDLERS
    // ============================================================
    const handleResetPassword = async (customer) => {
        if (!window.confirm(`${customer.name} ${customer.surname} kullanıcısının şifresini "123456!" olarak sıfırlamak istiyor musunuz?`)) return;
        setResettingPasswordId(customer.id);
        try {
            await usersAPI.update(customer.id, { password: "123456!" });
            toast.success("Şifre başarıyla sıfırlandı!");
        } catch (err) {
            toast.error("Şifre sıfırlanırken hata: " + (err?.message || "Bilinmeyen hata"));
        } finally {
            setResettingPasswordId(null);
        }
    };

    const handleToggleBlacklist = async (customer) => {
        const isBanning = !customer.is_blacklisted;
        const actionText = isBanning ? "kara listeye almak" : "kara listeden çıkarmak";
        if (!window.confirm(`${customer.name} ${customer.surname} müşterisini ${actionText} istediğinize emin misiniz?`)) return;
        try {
            await usersAPI.update(customer.id, { is_blacklisted: isBanning });
            setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, is_blacklisted: isBanning } : c));
            toast.success(`Müşteri ${isBanning ? "kara listeye alındı" : "kara listeden çıkarıldı"}.`);
        } catch (err) {
            toast.error("İşlem sırasında hata: " + err.message);
        }
    };

    const handleOpenModal = (customer) => {
        setFormData({
            id: customer.id,
            name: customer.name || "",
            surname: customer.surname || "",
            phone: customer.phone || "",
            email: customer.email || "",
            birth_date: customer.birth_date || "",
            tag: customer.tag || "",
        });
        setEditingCustomer(customer);
        setIsModalVisible(true);
        setModalError(null);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setEditingCustomer(null);
        setSubmitting(false);
        setModalError(null);
        setFormData({ name: "", surname: "", phone: "", email: "", birth_date: "", tag: "" });
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveEdit = async () => {
        setModalError(null);
        if (!formData.name || !formData.surname || !formData.phone) {
            setModalError("Ad, Soyad ve Telefon alanları zorunludur."); return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: formData.name, surname: formData.surname, phone: formData.phone,
                email: formData.email, birth_date: formData.birth_date || null,
                tag: formData.tag || null,
            };
            await usersAPI.update(editingCustomer.id, payload);
            setCustomers(prev => prev.map(c => c.id === editingCustomer.id ? { ...c, ...formData } : c));
            toast.success("Müşteri bilgileri güncellendi!");
            handleCloseModal();
        } catch (err) {
            setModalError(err.message || "Güncelleme sırasında bir hata oluştu.");
        } finally {
            setSubmitting(false);
        }
    };

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // ============================================================
    // TAB SAYAÇLARI
    // ============================================================
    const activeCount = useMemo(() => customers.filter(c => !c.is_blacklisted).length, [customers]);
    const blacklistedCount = useMemo(() => customers.filter(c => c.is_blacklisted).length, [customers]);

    // ============================================================
    // RENDER
    // ============================================================
    return (
        <div className="p-4 md:p-6 space-y-6">
            <ToastContainer position="bottom-right" theme="colored" toastClassName="!rounded-2xl !font-bold" />

            {/* Animasyon */}
            <style>{`
        @keyframes scaleIn { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }
        @keyframes fadeInUp { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        .cl-fadein { animation: fadeInUp 0.3s ease-out; }
      `}</style>

            {/* ====== SAYFA BAŞLIĞI ====== */}
            <div className="cl-fadein flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center shadow-lg shadow-brand-500/30">
                        <FiUsers className="text-white" size={22} />
                    </div>
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-navy-700 dark:text-white">Müşteri Yönetimi</h1>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">
                            Toplam <span className="font-bold text-navy-700 dark:text-white">{customers.length}</span> müşteri
                        </p>
                    </div>
                </div>
                <button
                    onClick={fetchCustomers}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 hover:bg-gray-50 dark:hover:bg-navy-700 transition-all shadow-sm"
                >
                    <FiRefreshCw size={15} className={loading ? "animate-spin" : ""} /> Yenile
                </button>
            </div>

            {/* ====== STAT KARTLARI ====== */}
            <div className="cl-fadein grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                    { label: "Toplam Müşteri", val: customers.length, icon: <FiUsers size={18} />, color: "brand" },
                    { label: "Aktif", val: activeCount, icon: <FiCheckCircle size={18} />, color: "green" },
                    { label: "Kara Liste", val: blacklistedCount, icon: <BsBan size={18} />, color: "red" },
                    { label: "Bugün Doğumlu", val: birthdayCustomers.length, icon: <FiGift size={18} />, color: "amber" },
                ].map((stat, i) => {
                    const colorMap = {
                        brand: "bg-brand-100 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400",
                        green: "bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400",
                        red: "bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400",
                        amber: "bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400",
                    };
                    return (
                        <div key={i} className="bg-white dark:bg-navy-800 rounded-2xl border border-gray-100 dark:border-navy-700 p-4 shadow-sm hover:shadow-md transition-all">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${colorMap[stat.color]}`}>
                                {stat.icon}
                            </div>
                            <div className="text-2xl font-extrabold text-navy-700 dark:text-white">{stat.val}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5">{stat.label}</div>
                        </div>
                    );
                })}
            </div>

            {/* ====== ARAMA + TAB ====== */}
            <div className="cl-fadein space-y-3">
                {/* Arama */}
                <div className="relative">
                    <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                        type="text"
                        placeholder="İsim, soyisim, e-posta veya telefon ara..."
                        value={q}
                        onChange={e => { setQ(e.target.value); setCurrentPage(1); }}
                        className="w-full bg-white dark:bg-navy-800 border border-gray-200 dark:border-navy-600 rounded-2xl pl-11 pr-4 py-3.5 text-sm text-navy-700 dark:text-white outline-none focus:border-brand-500 transition-all shadow-sm"
                    />
                    {q && (
                        <button
                            onClick={() => { setQ(""); setCurrentPage(1); }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                        >
                            <FiX size={14} />
                        </button>
                    )}
                </div>

                {/* Sekmeler */}
                <div className="flex gap-2 bg-gray-100 dark:bg-navy-900 p-1 rounded-2xl w-fit">
                    <button
                        onClick={() => setActiveTab("active")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all
              ${activeTab === "active"
                            ? "bg-white dark:bg-navy-800 text-navy-700 dark:text-white shadow-sm"
                            : "text-gray-500 dark:text-gray-400 hover:text-navy-700 dark:hover:text-white"}`}
                    >
                        <FiUser size={14} />
                        Aktif
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === "active" ? "bg-brand-100 dark:bg-brand-900/30 text-brand-500" : "bg-gray-200 dark:bg-navy-700 text-gray-500"}`}>
              {activeCount}
            </span>
                    </button>
                    <button
                        onClick={() => setActiveTab("blacklisted")}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all
              ${activeTab === "blacklisted"
                            ? "bg-white dark:bg-navy-800 text-red-600 dark:text-red-400 shadow-sm"
                            : "text-red-500/70 dark:text-red-500/60 hover:text-red-600 dark:hover:text-red-400"}`}
                    >
                        <BsBan size={14} />
                        Kara Liste
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === "blacklisted" ? "bg-red-100 dark:bg-red-900/30 text-red-500" : "bg-gray-200 dark:bg-navy-700 text-gray-500"}`}>
              {blacklistedCount}
            </span>
                    </button>
                </div>
            </div>

            {/* ====== MÜŞTERİ LİSTESİ KARTI ====== */}
            <div className="cl-fadein bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm overflow-hidden">
                {/* Kart Başlığı */}
                <div className="px-5 py-4 border-b border-gray-100 dark:border-navy-700 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-400 to-purple-500 flex items-center justify-center">
                            <FiFilter className="text-white" size={16} />
                        </div>
                        <div>
                            <h3 className="font-bold text-navy-700 dark:text-white text-base">
                                {activeTab === "active" ? "Aktif Müşteriler" : "Kara Liste"}
                            </h3>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                                {q ? `"${q}" için ${totalItems} sonuç` : `${totalItems} müşteri`}
                            </p>
                        </div>
                    </div>
                    {q && (
                        <span className="text-xs font-bold px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-900/20 text-brand-500 border border-brand-200 dark:border-brand-800/30">
              Filtreleniyor: "{q}"
            </span>
                    )}
                </div>

                {/* Yükleme */}
                {loading ? (
                    <div>
                        {/* Masaüstü skeleton */}
                        <div className="hidden md:block">
                            {[...Array(6)].map((_, i) => <SkeletonRow key={i} />)}
                        </div>
                        {/* Mobil skeleton */}
                        <div className="md:hidden p-4 space-y-3">
                            {[...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
                        </div>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                        <div className="w-14 h-14 rounded-2xl bg-red-100 dark:bg-red-900/20 flex items-center justify-center mb-4">
                            <FiAlertTriangle size={24} className="text-red-500" />
                        </div>
                        <p className="font-bold text-red-600 dark:text-red-400 mb-1">{error}</p>
                        <button onClick={fetchCustomers} className="mt-3 text-sm font-bold text-brand-500 hover:text-brand-600 underline">
                            Tekrar Dene
                        </button>
                    </div>
                ) : pagedCustomers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                        <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-navy-700 flex items-center justify-center mb-4">
                            <FiUsers size={24} className="text-gray-300 dark:text-gray-600" />
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 font-medium">Müşteri bulunamadı.</p>
                        {q && <p className="text-gray-400 dark:text-gray-600 text-xs mt-1">Farklı bir arama terimi deneyin.</p>}
                    </div>
                ) : (
                    <>
                        {/* MASAÜSTÜ TABLO */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                <tr className="bg-gray-50 dark:bg-navy-900/50">
                                    {["Müşteri Bilgisi", "Telefon", "Doğum Tarihi", "Kayıt Tarihi", "Durum", "İşlemler"].map(h => (
                                        <th
                                            key={h}
                                            className="px-5 py-3 text-left text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider"
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody>
                                {pagedCustomers.map(c => (
                                    <CustomerTableRow
                                        key={c.id}
                                        c={c}
                                        onEdit={handleOpenModal}
                                        onResetPassword={handleResetPassword}
                                        onToggleBlacklist={handleToggleBlacklist}
                                        resettingPasswordId={resettingPasswordId}
                                    />
                                ))}
                                </tbody>
                            </table>
                        </div>

                        {/* MOBİL KARTLAR */}
                        <div className="md:hidden p-4 space-y-3">
                            {pagedCustomers.map(c => (
                                <CustomerMobileCard
                                    key={c.id}
                                    c={c}
                                    onEdit={handleOpenModal}
                                    onResetPassword={handleResetPassword}
                                    onToggleBlacklist={handleToggleBlacklist}
                                    resettingPasswordId={resettingPasswordId}
                                />
                            ))}
                        </div>
                    </>
                )}

                {/* SAYFALAMA */}
                {!loading && !error && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        totalItems={totalItems}
                        pageStart={pageStart}
                        pageEnd={pageEnd}
                    />
                )}
            </div>

            {/* ====== BUGÜN DOĞUM GÜNÜ ====== */}
            <div className="cl-fadein bg-white dark:bg-navy-800 rounded-3xl border border-gray-100 dark:border-navy-700 shadow-sm overflow-hidden">
                {/* Başlık */}
                <div className="px-5 py-4 border-b border-gray-100 dark:border-navy-700 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                            <FiGift className="text-white" size={17} />
                        </div>
                        <div>
                            <h3 className="font-bold text-navy-700 dark:text-white text-base">Bugün Doğan Müşteriler</h3>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                                {birthdayCustomers.length > 0
                                    ? `${birthdayCustomers.length} müşterinin bugün doğum günü!`
                                    : "Bugün doğum günü olan müşteri yok."}
                            </p>
                        </div>
                    </div>
                    {birthdayCustomers.length > 0 && (
                        <span className="text-sm font-bold text-amber-600 dark:text-amber-400">🎂 {birthdayCustomers.length}</span>
                    )}
                </div>

                {/* İçerik */}
                <div className="p-4">
                    {birthdayCustomers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                            <div className="text-4xl mb-3">🎂</div>
                            <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">Bugün doğum günü olan müşteri bulunmuyor.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                            {birthdayCustomers.map(c => (
                                <BirthdayCard key={c.id} c={c} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ====== MODAL ====== */}
            <EditCustomerModal
                visible={isModalVisible}
                onClose={handleCloseModal}
                formData={formData}
                onChange={handleFormChange}
                onSave={handleSaveEdit}
                submitting={submitting}
                modalError={modalError}
                setModalError={setModalError}
            />
        </div>
    );
};

export default AdminCustomerList;